/**
 * belgeGruplari.js — firma belgelerinin iki grubu (Firma Belgeleri / Firma İSG Belgeleri) için
 * ortak yardımcı. Backlog #156 (backend: #123).
 *
 * Gruplar `GET /firma/{id}/profile-page?fuarId=` cevabının `belgeGruplari[]`ından gelir:
 * [{ kod: 'FIRMA'|'ISG', ad, status, tamam, belgeler: [{ fileTypeId, ad, zorunlu, fuaraOzel, status }] }]
 * — status'ler contract numaralamasıyla (0 Beklemede / 1 Onaylandı / 2 Reddedildi / null yok).
 * Hangi belgenin hangi grupta olduğu backend'deki firma_belge_grup tablosunda; burada hiçbir
 * fileTypeId sabit yazılmaz.
 *
 * Üç sayfa kullanıyor: firma tarafı `FirmaKartPage.jsx` (Evraklar sekmesi) ve
 * `FirmaAnasayfaListPage.jsx` (profil panelindeki özet), denetmen tarafı
 * `FirmaProfilIncelemePage.jsx` (Belgeler sekmesi).
 *
 * Tuzaklar:
 *  1. Grubu `kod` ile bul, ASLA `ad` ile değil — eski `firmaBelgeleri` anahtarı aslında İSG grubudur.
 *  2. `zorunlu: false` belge (bugün yalnızca Fuar Sözleşmesi) grubun durumuna SAYILMAZ, yalnızca
 *     yanında gösterilir. `fuaraOzel` belgenin durumu ancak `?fuarId=` verilirse dolu gelir.
 *  3. Grup satırları belge `id`si / dosya yolu TAŞIMAZ. Önizleme ya da onay/red için belge
 *     satırı `GET /firma/{id}/documents?fuarId=` listesinden `fileTypeId` ile eşlenir
 *     (bkz. grupBelgesininSatiri).
 *  4. Belgenin ekrandaki adı backend'in `ad`/`fileTypeName`i olmayabilir (bkz. belgeGorunenAd):
 *     ad yazdıran her yer onu bu yardımcıdan geçirmeli, yoksa sayfalar birbirinden ayrışır.
 *  5. `zorunlu: false` "gruba sayılmaz" DEMEK DEĞİL (#251): İşveren İSG Görevlendirmesi (99) da
 *     `zorunlu: false` gelir ama 88+89'un yerine geçen ikinci bir yoldur (`yerineGecer: [88, 89]`, grubun
 *     `yol`u 'ATAMA' | 'ISVEREN', `isverenIzni`). #483 (backend #482): denetmen anahtarı KALKTI — 99 her Yerli
 *     firmaya sunulur (bir fuar kuralı onu SUNULMAYAN / UYGULANAMAZ yapmadıkça) ve denetmen onaylayınca sayılır.
 *     Belgenin altına yazılacak notu HER ZAMAN belgeNotu üretir; `zorunlu`ya bakıp "Gruba sayılmaz" yazma.
 */

// Ekrandaki sıra ve başlıklar. Katalogda olmayan bir grup gelirse kendi `ad`ıyla sona eklenir.
import i18n from "../../i18n";

export const FIRMA_BELGE_GRUPLARI = [
  { kod: "FIRMA", get baslik() { return i18n.t("components:belgeGruplari.firmaBelgeleri"); } },
  { kod: "ISG", get baslik() { return i18n.t("components:belgeGruplari.firmaIsgBelgeleri"); } },
];

/**
 * Backlog #196 — backend'de adı kısa kalan belgelerin ekranda gösterilecek tam adı. 79 backend'de
 * 'Taahhütname'; ekranda 'Firma İSG Taahhütnamesi'. Backend adı bilerek değişmedi (tek bir etiket
 * için migration ağır bulundu), bu yüzden etiket burada, fileTypeId ile eşleniyor — ASLA adla.
 */
export const BELGE_GORUNEN_ADLARI = {
  // #528: 78 — the backend serves only the Turkish base name ("İmza Sirküsü"; the group catalogue reads file_type.file_type_name, not file_type_text),
  // so the English UI said it in Turkish. Same English wording as the rest of the UI (portal:imzaSirkusu, the card rule text).
  get 78() { return i18n.t("components:belgeGruplari.imzaSirkusu"); },
  get 79() { return i18n.t("components:belgeGruplari.firmaIsgTaahhutnamesi"); },
  // #535: 77 / 94 / 80 — same reason as 78: the group catalogue serves only the Turkish base names, so the English UI showed the cards of
  // the Documents tab and the "Not applicable: ..." line of the Home card in Turkish. Wording from the glossary (util/knowledgebase/i18n.md);
  // in Turkish the strings are the backend's own names.
  get 77() { return i18n.t("components:belgeGruplari.vergiLevhasi"); },
  get 94() { return i18n.t("components:belgeGruplari.faaliyetBelgesi"); },
  get 80() { return i18n.t("components:belgeGruplari.fuarSozlesmesi"); },
};

/** Belgenin ekranda yazılacak adı: istisna varsa o, yoksa backend'in verdiği `ad` / `fileTypeName`. */
export function belgeGorunenAd(fileTypeId, ad) {
  return BELGE_GORUNEN_ADLARI[fileTypeId] ?? ad;
}

/**
 * Bir grup belgesinin altına/yanına yazılacak kısa not (yoksa null) — üç durum:
 *  · başka belgelerin yerine geçen belge (`yerineGecer`, bugün İşveren İSG Görevlendirmesi):
 *    "İş Yeri Hekimi Ataması ve İş Güvenliği Uzmanı Ataması yerine geçer". Adlar aynı grubun
 *    kendi satırlarından okunur, hiçbir fileTypeId sabit yazılmaz;
 *  · yerine geçilen belge, grup o belge üzerinden TAMAMLANMIŞKEN (`tamam && yol === 'ISVEREN'`, #483: yalnızca
 *    bekleyen 99 "geçerli" demez) ve kendisi onaylı değilken: kırmızı "Eksik" kutusu grubun "Tamamlandı"sıyla
 *    çelişmesin diye. Backend #441 / #482'den beri onaylı 99 varken onaysız 88 / 89 zaten `uygulanamaz` gelir
 *    (ilk satır, gri "Uygulanamaz"); bu dal yedek;
 *  · diğer `zorunlu: false` belge (Fuar Sözleşmesi): "Gruba sayılmaz".
 */
export function belgeNotu(belge, grup = {}) {
  // #442: UYGULANAMAZ belge (backend #440 / #441) — nedenini söyler, başka bir not yazılmaz.
  if (belge.uygulanamaz) return belgeUygulanamazNedeni(belge);
  const belgeler = grup.belgeler || [];
  if (belge.yerineGecer?.length) {
    const adlar = belge.yerineGecer
      .map((id) => belgeler.find((b) => b.fileTypeId === id))
      .map((b, i) => (b ? belgeGorunenAd(b.fileTypeId, b.ad) : `#${belge.yerineGecer[i]}`));
    return i18n.t("components:belgeGruplari.yerineGecer", { v: adlar.join(" ve ") });
  }
  const yerineGecilen = belgeler.some((b) => b.yerineGecer?.includes(belge.fileTypeId));
  // #483: only once the group IS complete through the İşveren route. `yol` alone also reads 'ISVEREN' while 99 is merely
  // pending (backend: the better route wins, so a pending 99 over a rejected 88 gives yol ISVEREN, tamam false), and that
  // must not claim a replacement that has not happened. WAS: if (yerineGecilen && grup.yol === "ISVEREN" && belge.status !== 1) {
  if (yerineGecilen && grup.tamam && grup.yol === "ISVEREN" && belge.status !== 1) {
    return i18n.t("components:belgeGruplari.yerineIsverenGorevlendirmesiGecerli");
  }
  // #314 (2026-10-11): Vergi Levhası (77) ve Firma İSG Taahhütnamesi (79) yabancı firmada isteğe bağlı.
  if (belge.yabanciIcinIstegeBagli) return i18n.t("components:belgeGruplari.yabanciFirmaIcinIstegeBagli");
  return belge.zorunlu ? null : i18n.t("components:belgeGruplari.grubaSayilmaz");
}

/**
 * #442 (backend #440 / #441): belge bu firmaya UYGULANAMAZ — istenmez, sayılmaz, yüklenmez. "Eksik" (kırmızı), "isteğe bağlı"
 * ve "gruba sayılmaz" ile karışmasın diye ayrı, nötr bir durumdur. Backend alanları: `uygulanamaz` (boolean) ve
 * `uygulanamazNedeni`: 'YABANCI_FIRMA' (yabancı firmada Vergi Levhası ve Faaliyet Belgesi) ya da 'ISVEREN_GOREVLENDIRMESI'
 * (onaylı İşveren İSG Görevlendirmesi varken, kendisi onaylı olmayan 88 / 89) ya da 'BELGE_KURALI' (#430: yabancı olmayan firmada bir belge kuralı).
 * Hiçbir fileTypeId burada sabit yazılmaz.
 */
export function belgeUygulanamazMi(belge) {
  return belge?.uygulanamaz === true;
}

/** "Uygulanamaz" durum rozeti ({ label, color }), kutucuk ve rozetlerde ortak. */
export function uygulanamazDurumu() {
  return { label: i18n.t("components:belgeGruplari.uygulanamaz"), color: "secondary" };
}

/** Neden uygulanamaz — ipucu / not metni. Bilinmeyen bir neden de kısa bir "Uygulanamaz" okunur. */
export function belgeUygulanamazNedeni(belge = {}) {
  if (belge.uygulanamazNedeni === "YABANCI_FIRMA") return i18n.t("components:belgeGruplari.uygulanamazYabanci");
  if (belge.uygulanamazNedeni === "ISVEREN_GOREVLENDIRMESI") return i18n.t("components:belgeGruplari.uygulanamazIsveren");
  // #430: a company document rule (fuar_firma_belge_kural) says so, for a company that is not a foreign one
  if (belge.uygulanamazNedeni === "BELGE_KURALI") return i18n.t("components:belgeGruplari.uygulanamazKural");
  return i18n.t("components:belgeGruplari.uygulanamaz");
}

/**
 * #442: bir grubun UYGULANAMAZ belgelerinin adları tek satırda ("Uygulanamaz: Vergi Levhası, Faaliyet Belgesi"); yoksa null.
 * Grup rozeti yanında yazılır: belgeler sayıma girmediği için grup "Tamamlandı" derken neden eksik kare görünmediği anlaşılsın.
 */
export function grupUygulanamazMetni(grup = {}) {
  const adlar = (grup.belgeler || []).filter(belgeUygulanamazMi).map((b) => belgeGorunenAd(b.fileTypeId, b.ad));
  return adlar.length ? i18n.t("components:belgeGruplari.grupUygulanamaz", { v: adlar.join(", ") }) : null;
}

/** belgeGruplari[] → [{ ...grup, baslik }], FIRMA_BELGE_GRUPLARI sırasıyla. */
export function siraliBelgeGruplari(belgeGruplari = []) {
  const bilinen = FIRMA_BELGE_GRUPLARI
    .map(({ kod, baslik }) => {
      const grup = belgeGruplari.find((g) => g.kod === kod);
      return grup ? { ...grup, baslik } : null;
    })
    .filter(Boolean);
  const digerleri = belgeGruplari
    .filter((g) => !FIRMA_BELGE_GRUPLARI.some((b) => b.kod === g.kod))
    .map((g) => ({ ...g, baslik: g.ad || g.kod }));
  return [...bilinen, ...digerleri];
}

/**
 * #511: Yabancı Katılımcı'dan YALNIZCA bu iki firma belgesi istenir — İmza Sirküsü (78) ve İSG Taahhütnamesi (79). Backend zaten
 * Vergi Levhası (77) ve Faaliyet Belgesi'ni (94) "Uygulanamaz" işaretler, 88 / 89 / 99'u hiç göndermez (#440; kart kapısı #507);
 * Fuar Sözleşmesi (80) ise yüklenemeyen isteğe bağlı bir satırdır. Sade görünüm hepsini bırakıp bu ikisini gösterir. Bu, "grup üyeliği
 * backend'den" kuralının bilinçli istisnasıdır: kullanıcı iki belge istedi (Deniz 2026-10-19), liste de onlar.
 * #704: yalnızca GERÇEK Yabancı Katılımcı seti (yabanciKatilimciMi). Sade panel modunda başka bir firma bu listeyi değil, backend'in kendi
 * uyruğuna ve rolüne göre gönderdiği grupları görür.
 */
export const YABANCI_KATILIMCI_FIRMA_BELGELERI = [78, 79];

/** `siraliBelgeGruplari` — yalnızca Yabancı Katılımcı'nın iki belgesi kalır; belgesi kalmayan grup düşer. */
export function yabanciKatilimciBelgeGruplari(belgeGruplari = []) {
  return siraliBelgeGruplari(belgeGruplari)
    .map((g) => ({ ...g, belgeler: (g.belgeler || []).filter((b) => YABANCI_KATILIMCI_FIRMA_BELGELERI.includes(b.fileTypeId)) }))
    .filter((g) => g.belgeler.length > 0);
}

/**
 * #528 (2026-10-21, Deniz / Bilge): Yabancı Katılımcı'nın Evraklar sekmesi iki bölüm: ÜSTTE istenen iki belge — İSG Taahhütnamesi (79)
 * en üstte, sonra İmza Sirküsü (78) — ve ALTTA geri kalan her firma belgesi (Vergi Levhası, Faaliyet Belgesi, Fuar Sözleşmesi...),
 * isteğe bağlı olarak işaretli ve varsayılan KAPALI bir "Firma Evrakları" kartında. `grup` her belgenin kendi grubudur
 * (belgeNotu grubu okur). WAS (#511): yalnızca 78 + 79 gösterilir, gerisi hiç çizilmezdi (yabanciKatilimciBelgeGruplari).
 */
export const ISG_TAAHHUTNAMESI_FILE_TYPE_ID = 79;
export const IMZA_SIRKUSU_FILE_TYPE_ID = 78;
export const YABANCI_KATILIMCI_EVRAK_SIRASI = [ISG_TAAHHUTNAMESI_FILE_TYPE_ID, IMZA_SIRKUSU_FILE_TYPE_ID];

export function yabanciKatilimciEvrakBolumleri(belgeGruplari = []) {
  const tum = siraliBelgeGruplari(belgeGruplari).flatMap((grup) => (grup.belgeler || []).map((belge) => ({ belge, grup })));
  const gerekli = YABANCI_KATILIMCI_EVRAK_SIRASI
    .map((id) => tum.find((x) => x.belge.fileTypeId === id))
    .filter(Boolean);
  const digerleri = tum.filter((x) => !YABANCI_KATILIMCI_EVRAK_SIRASI.includes(x.belge.fileTypeId));
  return { gerekli, digerleri };
}

/** Üstteki "istenen belgeler" bölümünün toplam durumu: `grupDurumInfo`nun okuduğu şekilde sentetik bir grup. */
export function gerekliBelgelerDurumu(gerekli = []) {
  const belgeler = gerekli.map((x) => x.belge);
  return grupDurumInfo({
    belgeler,
    tamam: belgeler.length > 0 && belgeler.every((b) => b.status === 1),
    status: belgeler.some((b) => b.status === 2) ? 2 : null,
  });
}

/** Yabancı Katılımcı'nın iki firma belgesi küçük kutular için (`grupBelgeKareleri` biçiminde), grup sırasıyla. */
export function yabanciKatilimciFirmaKareleri(belgeGruplari) {
  return yabanciKatilimciBelgeGruplari(belgeGruplari || []).flatMap(grupBelgeKareleri);
}

/**
 * Grubun toplam durumu → { label, color } rozeti. Backend'in status 0'ı "bir şey yüklendi ama
 * hepsi onaylı değil" demek; eksik zorunlu belge varken "Beklemede" demek yanıltıcı olur.
 */
export function grupDurumInfo(grup = {}) {
  // #314: yabancı firmanın İSG grubu istenmez (backend gerekli: false, tamam: true, status null).
  if (grup.gerekli === false) return { label: i18n.t("components:belgeGruplari.gerekmiyorYabanciFirma"), color: "secondary" };
  // #442: gruptaki her belge uygulanamazsa grup da "Uygulanamaz" okunur (bugün böyle bir grup yok; #430 verisi getirebilir).
  if ((grup.belgeler || []).length > 0 && grup.belgeler.every(belgeUygulanamazMi)) return uygulanamazDurumu();
  if (grup.tamam) return { label: i18n.t("components:belgeGruplari.tamamlandi"), color: "success" };
  if (grup.status === 2) return { label: i18n.t("components:belgeGruplari.reddedilenBelgeVar"), color: "danger" };
  const eksikVar = (grup.belgeler || []).some((b) => b.zorunlu && b.status == null);
  return eksikVar ? { label: i18n.t("components:belgeGruplari.eksik"), color: "danger" } : { label: i18n.t("components:belgeGruplari.onayBekliyor"), color: "warning" };
}

/**
 * FIRMA grubu "Eksik" / "Onay Bekliyor" iken kart için YETERLİ olabilir (backend #276): işveren yolu üç
 * belgeden yalnızca birinin onaylı olmasını ister (`kartIcinGerekli`, bugün 1), grubun kendi durumu ise hâlâ
 * üçünü ister. Yeşil bir İşveren kartı yanında kırmızı bir grup hata gibi okunmasın diye grubun yanına
 * "Kart için yeterli" yazılır. Alan yoksa (ISG grubu, eski cevap) null.
 */
export function grupKartNotu(grup = {}) {
  return grup.kod === "FIRMA" && !grup.tamam && grup.kartIcinYeterli ? i18n.t("components:belgeGruplari.kartIcinYeterli") : null;
}

/** `kod`u 'ISG' olan grup (profile-page `belgeGruplari[]`ı ya da bir personel satırının `firmaBelgeGruplari[]`ı); yoksa null. */
export function isgGrubu(belgeGruplari) {
  return (belgeGruplari || []).find((g) => g.kod === "ISG") ?? null;
}

/**
 * Backlog #258 — bir grubun belgeleri küçük kutucuklar/rozetler için: [{ fileTypeId, ad, status, note, ok }].
 * Üç sabit anahtar (79/88/89) yerine grubun kendi satırlarından kurulur, yani İşveren İSG Görevlendirmesi
 * (99) yalnızca grup onu taşıyorsa görünür (#483: denetmen anahtarı kalktı, bugün her Yerli firmada taşır,
 * Yabancı firmada ya da bir fuar kuralı gizlediğinde taşımaz) ve hiçbir fileTypeId sabit yazılmaz.
 * `ok`: grup tamamsa hepsi sağlanmış sayılır (İşveren yolunda onaysız 88/89 ve isteğe bağlı 99 dahil —
 * not zaten nedenini söyler), değilse yalnızca onaylılar.
 */
export function grupBelgeKareleri(grup = {}) {
  return (grup.belgeler || []).map((b) => ({
    fileTypeId: b.fileTypeId,
    ad: belgeGorunenAd(b.fileTypeId, b.ad),
    status: b.status ?? null,
    note: belgeNotu(b, grup),
    // #442: UYGULANAMAZ kare nötr gri çizilir ve sayılmaz; hiçbir şey eksik olmadığı için `ok` yine true okunur.
    uygulanamaz: belgeUygulanamazMi(b),
    ok: !!grup.tamam || b.status === 1 || belgeUygulanamazMi(b),
  }));
}

/**
 * Bir grup belgesinin `GET /documents` listesindeki satırı — grubun okuduğu satırın aynısı:
 * aynı fileTypeId, taşeron satırı değil (ilgiliFirmaId boş), fuara özel belgede o fuarın,
 * firma-geneli belgede fuarsız satır; birden çoksa en yeni (en büyük id). Yoksa null.
 * #212: iki firma sayfası artık GET /evraklar'ın hazır eşlediği `belge.dosya`yı okuyor; çağıranı kalmadı.
 */
export function grupBelgesininSatiri(documents = [], belge, fuarId) {
  return documents
    .filter((d) => d.fileTypeId === belge.fileTypeId && d.ilgiliFirmaId == null)
    .filter((d) => (belge.fuaraOzel ? Number(d.fuarId) === Number(fuarId) : d.fuarId == null))
    .reduce((enYeni, d) => (!enYeni || d.id > enYeni.id ? d : enYeni), null);
}

/**
 * #388: bir firmanın FUARA ÖZEL belgeleri (bugün yalnızca Fuar Sözleşmesi) grup cevabından, `GET /documents` okumadan:
 * [{ key, label, status: 'onayli' | 'yuklendi' | 'eksik' }]. Stand Durumu'nun evrak özeti (standDurumuService.evrakDurumuOk)
 * bunu okur. Başka firmanın belgeleri `GET /documents`ten gelmez (yalnızca kendi firması ya da personel okur); profile-page ise
 * ortak standı olan firmaya (kitle ORTAK) belgeGruplari'nı verir. Satırı olmayan belge (`status` null) hiç listelenmez:
 * eski `/documents` okuması da satırı yoksa boş dönerdi. Grup status'ü 0 Beklemede / 1 Onaylandı / 2 Reddedildi.
 * Hiçbir fileTypeId sabit yazılmaz: fuaraOzel bayrağı yeter.
 */
export function fuaraOzelBelgeler(belgeGruplari = []) {
  return (belgeGruplari || [])
    .flatMap((g) => g.belgeler || [])
    .filter((b) => b.fuaraOzel && b.status != null)
    .map((b) => ({
      key: `belge${b.fileTypeId}`,
      label: belgeGorunenAd(b.fileTypeId, b.ad),
      status: b.status === 1 ? "onayli" : b.status === 0 ? "yuklendi" : "eksik",
    }));
}
